import { useCallback } from 'react'

import { type SanityFrameBuilderModule } from './data/sanity/queries/types'
import {
  addFrameToCartEventName,
  openFrameStepEventName,
  triggerGoogleTagManagerEvent,
} from './lib/analytics'
import { type Step, configurationStepTitles } from './lib/step-definition'
import {
  type VariantLineItem,
  FrameBuilderCartProvider,
} from './contexts/cart-context'
import { FrameBuilderConfigurationProvider } from './contexts/configuration-context'
import { FrameBuilderDefaultFrameProvider } from './contexts/default-frame-context'
import { FrameBuilderLanguageProvider } from './contexts/langauge-context'
import { FrameBuilderSelectionProvider } from './contexts/selection-context'
import { FrameBuilderShopProvider } from './contexts/shop-context'
import { FrameBuilderStateProvider } from './contexts/state-context'
import { FrameBuilderStringsProvider } from './contexts/strings-context'
import { FrameBuilderValidationProvider } from './contexts/validation-context'

import FrameBuilderComponent from './components'

type FrameBuilderProps = Pick<
  SanityFrameBuilderModule,
  | 'strings'
  | 'defaultFrame'
  | 'servicePrices'
  | 'profiles'
  | 'textures'
  | 'passepartoutSizes'
  | 'passepartoutColors'
  | 'glass'
  | 'spacerSizes'
  | 'spacerColors'
  | 'standardSizes'
  | 'extraServices'
  | 'showExtrasStep'
  | 'showProgressBar'
  | 'updateHash'
> & {
  locale: string
  currency?: string
  taxRate: number
  isCartProductAdding: boolean
  addItemsToCart: (variantLineItems: VariantLineItem[]) => Promise<boolean>
}

const FrameBuilder = ({
  strings,
  defaultFrame,
  servicePrices,
  profiles,
  textures,
  passepartoutSizes,
  passepartoutColors,
  glass,
  spacerSizes,
  spacerColors,
  standardSizes,
  extraServices,
  showExtrasStep,
  showProgressBar,
  updateHash,
  locale,
  currency,
  taxRate,
  isCartProductAdding,
  addItemsToCart,
}: FrameBuilderProps) => {
  const handleOpenStep = useCallback((step: Step) => {
    triggerGoogleTagManagerEvent(openFrameStepEventName, {
      step_title: configurationStepTitles[step],
    })
  }, [])
  const handleAddToCart = useCallback(() => {
    triggerGoogleTagManagerEvent(addFrameToCartEventName)
  }, [])

  return (
    <FrameBuilderStringsProvider strings={strings}>
      <FrameBuilderLanguageProvider locale={locale}>
        <FrameBuilderShopProvider currency={currency} taxRate={taxRate}>
          <FrameBuilderCartProvider
            isCartProductAdding={isCartProductAdding}
            addItemsToCart={addItemsToCart}
          >
            <FrameBuilderDefaultFrameProvider
              defaultFrame={defaultFrame}
              profiles={profiles}
              textures={textures}
              passepartoutSizes={passepartoutSizes}
              passepartoutColors={passepartoutColors}
              glass={glass}
              spacerSizes={spacerSizes}
              spacerColors={spacerColors}
              extraServices={extraServices}
              showExtrasStep={!!showExtrasStep}
            >
              <FrameBuilderConfigurationProvider
                standardSizes={standardSizes}
                profiles={profiles}
                textures={textures}
                passepartoutSizes={passepartoutSizes}
                passepartoutColors={passepartoutColors}
                glass={glass}
                spacerSizes={spacerSizes}
                spacerColors={spacerColors}
                extraServices={extraServices}
                servicePrices={servicePrices}
              >
                <FrameBuilderStateProvider updateHash={!!updateHash}>
                  <FrameBuilderValidationProvider>
                    <FrameBuilderSelectionProvider>
                      <FrameBuilderComponent
                        showExtrasStep={!!showExtrasStep}
                        showProgressBar={!!showProgressBar}
                        onOpenStep={handleOpenStep}
                        onAddToCart={handleAddToCart}
                      />
                    </FrameBuilderSelectionProvider>
                  </FrameBuilderValidationProvider>
                </FrameBuilderStateProvider>
              </FrameBuilderConfigurationProvider>
            </FrameBuilderDefaultFrameProvider>
          </FrameBuilderCartProvider>
        </FrameBuilderShopProvider>
      </FrameBuilderLanguageProvider>
    </FrameBuilderStringsProvider>
  )
}

export default FrameBuilder
